/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @JobTitle: Full Stack Developer
 *
 * This component renders a table for displaying user access logs, including user details,
 * device information, access status, and access methods.
 *
 * @param {Object} data - The dataset containing user access logs.
 * @returns {Object} - Table properties and functions to manage table state.
 */

import {useDispatch, useSelector} from "react-redux";
import { useTranslation } from "react-i18next";
import React from "react";
import CustomTh from "../../../../components/table/CustomTh";
import CustomTd from "../../../../components/table/CustomTd";
import CustomImageTable from "../../../../components/CustomImageTable";
import TableFunction from "../../../../components/table/TableFunction";
import checkIcon from "../../../../assets/icon/checkIcon.svg";
import cancelIcon from "../../../../assets/icon/cancelIcon.svg";
import {DATE_TIME_FORMAT_NAME_MODEL, EMPTY_VALUES, getDateFormat} from "../../../../Apis/functions";
import {SearchUserZone, SimpleSearch} from "../../../../reduxToolkit/Search/SearchApi";
import {TABLES} from "../../../../Apis/Tables";
import {CRITERIA_MODEL, DEFAULT_LEFT, SEARCH_MODEL} from "../../../../components/table/Model/TableModel";
import {getAccessMethodIcon, getAccessMethodLabel} from "../../../../enums/accessMethodEnum";
import  IconAvatarTable from '../../../../assets/IconsTables/ic-avatar.svg';

export const SectionAccessUserTabTableApp = ({
                                                 setBodyForExport,
                                                 setCriteria,
                                                 finalArray,
                                                 setExpandImage,
                                                 searchElement,
                                                 setSearchResults,
                                                 searchResults,
                                                 modified,
                                                 setModified
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const tableId = "tbl_accss_access";
    const searchId = "txt_accss_search_access";
    const option =`${TABLES.USER_ACCESS_LOG}`;
    const leftPosition = DEFAULT_LEFT;
    const { user } = useSelector((state) => state.authenticatioauthennSlice);
    const userId = user?.data?.id;

    /**
     * Generates table headers.
     * @returns {Array} - Array of table header components.
     */
    const getThead = () => [
        <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head">{t('user')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('email')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('phone_number')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('user_type')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('accessDate')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('device')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('zone')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('successful_attempt')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('access_method')}</CustomTh>,
    ];

    /**
     * Generates table body content based on data.
     * @returns {Object} - Object containing body content and data array.
     */

    const getTbody = () => {
        if (!searchResults || searchResults?.content?.length === 0) return {
            body: [], data: []
        };

        let body = searchResults?.content?.map((item, index) => {
            let imageAvatar = (item?.image !== null ? `data:image/jpeg;base64,${item?.image}` :  IconAvatarTable)

            return (
                <>
                    <CustomTd tableId={tableId} tooltip={item?.fullName || `${item?.name} ${item?.lastName} ${item?.secondLastName || ''}`} fixed={true} left={leftPosition} classes={'first-column'}>
                        <span className='text-ellipsis'>
                            <CustomImageTable
                                text={item?.fullName || `${item?.name} ${item?.lastName} ${item?.secondLastName || ''}`}
                                image={imageAvatar}
                                classNameImage={'rounded-circle'}
                                buttonImg={'btn_saut_expand_image_' + index}
                                click={(e, image) => {
                                    if (item?.image !== null){
                                        setExpandImage(image);
                                    }
                                }}
                            />
                        </span>
                    </CustomTd>
                    <CustomTd tableId={tableId}>{item?.email || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>{item?.phoneNumber || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>{item?.userTypeName || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>
                        {getDateFormat(item?.accessDate, null, DATE_TIME_FORMAT_NAME_MODEL.dateTimeWithSeconds) ||
                            EMPTY_VALUES.DEFAULT}
                    </CustomTd>
                    <CustomTd tableId={tableId}>{item?.deviceName || item?.entryDeviceName}</CustomTd>
                    <CustomTd tableId={tableId}>{item?.zoneName || item?.entryZoneName}</CustomTd>
                    <CustomTd tableId={tableId}>
                    <span className="text-ellipsis">
                        <CustomImageTable image={item?.successfulAttempt ? checkIcon : cancelIcon}/>
                    </span>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                    <span className="text-ellipsis icon-hover-secondary-500">
                        <CustomImageTable
                            isTooltip={true}
                            tooltip={getAccessMethodLabel(item?.accessMethodId || item?.entryAccessMethodId,t)}
                            image={getAccessMethodIcon(item?.accessMethodId || item?.entryAccessMethodId)}
                        />
                    </span>
                    </CustomTd>
                </>
            )

        });
        return {
            body,
            data :  searchResults?.content};
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page, { searchElement }) => {
        const body = SEARCH_MODEL.setObject({page, size: rowsPerPage, model:option, key:searchElement});
        setBodyForExport({ ...body,  userId });
        dispatch(SimpleSearch({ body })).then(
            ({payload: {data: { data, success }}}) => {
                if (success === true) {
                    setSearchResults(data);
                }
            },
        );
    }

    /**
     * Executes a paginated search request using advanced criteria.
     *
     * Builds the criteria list based on the advanced search (finalArray),
     * appends user criteria, and dispatches the search action through Redux.
     * Updates the results and resets the selected list once the data is returned.
     */
    const getDataAdvancedSearch = (page) => {
        const body = SEARCH_MODEL.setObject({
            page,
            size:rowsPerPage,
            model:option,
            rootGroup: CRITERIA_MODEL.set(finalArray),
            userToFindId: userId
        });
        setCriteria(body);
        dispatch(SearchUserZone({ option, body })).then(({ payload: {data: { data }}}) => {
            setSearchResults(data);
        });
    }

    /**
     * Table functions and pagination controls.
     */
    const { thead, tbody, pagination, page, rowsPerPage} = TableFunction({
        data:searchResults,
        getThead,
        getTbody,
        hasContainer: true,
        labelRowsPerPage: 'user_access_per_page',
        fixedPagination: false,

        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
        getDataAdvancedSearch,//New - apply only with advanced search
        finalArray,//New - apply only with advanced search
    });

    return {
        tableId,
        searchId,
        thead,
        tbody,
        pagination,
        page,
        rowsPerPage,
        getData
    };
};
